import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import {
  closeOverlay,
  openOverlay,
  type SourceRegisteredTablesOverlayData,
} from "@/features/adp/state/overlaySlice";
import { useRegisteredSourceFilesQuery } from "@/features/adp/queries/useSourceQueries";
import { useFinalizeRegisteredSourceMutation } from "@/features/adp/hooks/useFinalizeRegisteredSourceMutation";
import type {
  RegisteredSourceFile,
  RegisteredSourceFileField,
} from "@/features/adp/services/source/source.types";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Input } from "@/ui/input";
import { RadioGroup, RadioGroupItem } from "@/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { Skeleton } from "@/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Plus,
  Search,
} from "lucide-react";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import styles from "./RegisteredSourcesTableList.module.scss";

const ROWS_PER_PAGE_OPTIONS = [2, 5, 10, 25];
const FIELD_ROWS_PER_PAGE = 7;

function isSourceRegisteredTablesOverlayData(
  value: unknown,
): value is SourceRegisteredTablesOverlayData {
  return Boolean(value && typeof value === "object");
}

/**
 * Map a registered-source file's persisted fields into the
 * `source_files` shape that `DataProfilingConfirmation` reads from node
 * session state.
 */
function buildSourceFilesPayload(
  file: RegisteredSourceFile,
): Array<{
  source_file_id: number;
  source_file_field_id: number | undefined;
  field_name: string;
  field_data_type: string;
}> {
  return file.fields.map((field: RegisteredSourceFileField) => ({
    source_file_id: file.source_file_id,
    source_file_field_id: field.source_file_field_id,
    field_name: field.field_name,
    field_data_type: field.data_type,
  }));
}

/**
 * Parse the combined `endpoint` string a registered source stores (e.g.
 * `52.36.76.75:7654?automated_test` for postgres) into separate host / port /
 * schema parts. `port` must come out as a number to satisfy the backend's
 * `GetColumnsDatabaseDto` validation (port: number → NaN otherwise).
 *
 * Handles these common shapes:
 *   `host:port`
 *   `host:port?schema`
 *   `host:port/database` (database path is discarded — `database` is its own
 *      form field, we shouldn't overwrite it from the path)
 *   `host:port/database?schema`
 *
 * Non-conforming endpoints (e.g. `mongodb+srv://...`, IPv6 `[::1]:5432`) fall
 * through unchanged in `host` with `port: null` — caller accepts that for
 * non-RDBMS shapes.
 */
function parseRegisteredEndpoint(endpoint: string): {
  host: string;
  port: number | null;
  schema: string;
} {
  const trimmed = endpoint.trim();
  if (!trimmed) return { host: '', port: null, schema: '' };

  // Split on first colon, peel off leading digits as port. Anything else
  // after the port (path / query) is parsed below.
  const colonIdx = trimmed.indexOf(':');
  if (colonIdx <= 0) return { host: trimmed, port: null, schema: '' };

  const host = trimmed.slice(0, colonIdx);
  const afterColon = trimmed.slice(colonIdx + 1);
  const portMatch = afterColon.match(/^(\d+)/);
  if (!portMatch) return { host: trimmed, port: null, schema: '' };

  const port = Number(portMatch[1]);
  const afterPort = afterColon.slice(portMatch[1].length);

  // Schema lives after `?`. Any path segment between port and `?` is discarded.
  const queryIdx = afterPort.indexOf('?');
  const schema = queryIdx === -1 ? '' : afterPort.slice(queryIdx + 1);

  return { host, port, schema };
}

const RegisteredSourcesTableList = () => {
  const dispatch = useAppDispatch();
  const { isOpen, type, data } = useAppSelector((state) => state.overlay);
  const finalizeRegisteredSource = useFinalizeRegisteredSourceMutation();

  const [searchValue, setSearchValue] = useState("");
  const [expandedTableIds, setExpandedTableIds] = useState<number[]>([]);
  const [selectedTableId, setSelectedTableId] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(2);
  const [fieldPageByTable, setFieldPageByTable] = useState<Record<number, number>>({});

  const isVisible = isOpen && type === "source-registered-tables-list";
  const modalData = isVisible && isSourceRegisteredTablesOverlayData(data) ? data : null;
  const sourceDataSourceId = modalData?.sourceDataSourceId ?? null;

  const { data: sourceFiles = [], isPending, error } = useRegisteredSourceFilesQuery(
    sourceDataSourceId,
    undefined,
    { enabled: isVisible && typeof sourceDataSourceId === "number" && sourceDataSourceId > 0 },
  );

  const visibleTables = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) return sourceFiles;
    return sourceFiles.filter((table) => table.table_name.toLowerCase().includes(normalizedSearch));
  }, [searchValue, sourceFiles]);

  const totalPages = Math.max(1, Math.ceil(visibleTables.length / rowsPerPage));
  const pagedTables = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return visibleTables.slice(startIndex, startIndex + rowsPerPage);
  }, [page, rowsPerPage, visibleTables]);

  const isEveryVisibleTableExpanded =
    pagedTables.length > 0 &&
    pagedTables.every((table) => expandedTableIds.includes(table.source_file_id));

  useEffect(() => {
    if (isVisible) {
      setSearchValue("");
      setExpandedTableIds([]);
      setSelectedTableId(undefined);
      setPage(1);
      setRowsPerPage(2);
      setFieldPageByTable({});
    }
  }, [isVisible]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleClose = () => dispatch(closeOverlay());

  const handleBack = () => {
    if (!modalData) {
      handleClose();
      return;
    }
    dispatch(
      openOverlay({
        type: "source-picker",
        data: {
          nodeId: modalData.nodeId ?? null,
          sourceId: modalData.pickerSourceId ?? null,
          sourceType: modalData.pickerSourceType ?? null,
        },
      }),
    );
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setPage(1);
  };

  const handleRowsPerPageChange = (value: string) => {
    setRowsPerPage(Number(value));
    setPage(1);
  };

  const toggleTable = (table: RegisteredSourceFile) => {
    setExpandedTableIds((current) =>
      current.includes(table.source_file_id)
        ? current.filter((id) => id !== table.source_file_id)
        : [...current, table.source_file_id],
    );
  };

  const toggleExpandAll = () => {
    const visibleIds = pagedTables.map((table) => table.source_file_id);
    setExpandedTableIds((current) => {
      if (isEveryVisibleTableExpanded) {
        return current.filter((id) => !visibleIds.includes(id));
      }
      return Array.from(new Set([...current, ...visibleIds]));
    });
  };

  const handleFieldPageChange = (tableId: number, delta: number, maxPages: number) => {
    setFieldPageByTable((current) => {
      const next = Math.max(1, Math.min(maxPages, (current[tableId] ?? 1) + delta));
      return { ...current, [tableId]: next };
    });
  };

  const handleAddNewTable = () => {
    if (!modalData?.sourceDataSourceId || !modalData?.sourceInstance) {
      toast.error("Cannot open Add New Table — source identity is missing.");
      return;
    }
    dispatch(
      openOverlay({
        type: "source-add-new-table",
        data: {
          nodeId: modalData.nodeId ?? null,
          sourceDataSourceId: modalData.sourceDataSourceId,
          sourceInstance: modalData.sourceInstance,
          sourceName: modalData.sourceName,
          dataSourceType: modalData.dataSourceType,
          parentOverlay: modalData,
        },
      }),
    );
  };

  const selectedTable = useMemo(
    () => sourceFiles.find((table) => String(table.source_file_id) === selectedTableId) ?? null,
    [selectedTableId, sourceFiles],
  );

  const handleSave = () => {
    if (!selectedTable || !modalData?.nodeId) return;
    // Build the connection payload the Explorer dialog needs on revisit
    // (gear icon → "configuration-details" completed → ExplorerDialog).
    // The backend's `/connections/columns` whitelists fields strictly per
    // `GetColumnsDatabaseDto`: extras (database_name/endpoint/secret_key2)
    // → 400, and `port` must be a number — so we parse the combined endpoint
    // string and strip the rejected keys.
    //
    // Strategy: start from `registeredSourceValues` (so non-RDBMS sources keep
    // their bucket_name / warehouse / region / etc. that buildRegisteredSource
    // -Values pipes through), then override host/port/schema from the parsed
    // endpoint and drop the known-rejected aliases. Avoids per-source-type
    // whitelists going stale as the connector catalog grows.
    const values = modalData.registeredSourceValues ?? {};
    const parsed = parseRegisteredEndpoint(values.host ?? values.endpoint ?? '');
    const REJECTED_KEYS = new Set(['database_name', 'endpoint', 'secret_key2']);
    const submittedSourcePayload: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) {
      if (REJECTED_KEYS.has(k)) continue;
      submittedSourcePayload[k] = v;
    }
    submittedSourcePayload.type = modalData.sourceInstance ?? values.type ?? '';
    submittedSourcePayload.source_name = modalData.sourceName ?? values.source_name ?? '';
    submittedSourcePayload.host = parsed.host || values.host || '';
    // Schema may be empty for non-RDBMS sources or when the registered
    // endpoint didn't carry one. Don't send an empty string — backends often
    // treat absence as "use the default schema" but reject `""` as invalid.
    const resolvedSchema = parsed.schema || values.schema || '';
    if (resolvedSchema) {
      submittedSourcePayload.schema = resolvedSchema;
    } else {
      delete submittedSourcePayload.schema;
    }
    // Port: prefer parsed, then a legacy form-level `port` field if present.
    // For RDBMS sources the backend rejects requests without a numeric port —
    // having either source resolve avoids the silent drop.
    if (parsed.port != null && Number.isFinite(parsed.port)) {
      submittedSourcePayload.port = parsed.port;
    } else {
      const fallbackPort = Number(values.port);
      if (Number.isFinite(fallbackPort) && fallbackPort > 0) {
        submittedSourcePayload.port = fallbackPort;
      }
    }

    // Surface a console warning for RDBMS-shaped sources missing a port — the
    // backend will 400 on `/connections/columns` with no client diagnosis
    // otherwise. Schema absence is sometimes legitimate (postgres default), so
    // only port is flagged. dataSourceType is `DATABASE` for SQL connectors.
    const isRdbmsLikeSource =
      (modalData.dataSourceType ?? '').toUpperCase().includes('DATABASE') ||
      (modalData.dataSourceType ?? '').toUpperCase() === 'RDBMS';
    if (isRdbmsLikeSource && submittedSourcePayload.port == null) {
      console.warn(
        'RegisteredSourcesTableList: RDBMS source has no port in submittedSourcePayload — `/connections/columns` will likely 400. ' +
          'Check the registered endpoint shape returned by the API.',
        { sourceInstance: modalData.sourceInstance, endpoint: values.host ?? values.endpoint },
      );
    }
    finalizeRegisteredSource.mutate({
      nodeId: modalData.nodeId,
      sourceDataSourceId: modalData.sourceDataSourceId ?? null,
      rooId: modalData.rooId ?? null,
      sourceName: modalData.sourceName ?? null,
      tableName: selectedTable.table_name,
      sourceFiles: buildSourceFilesPayload(selectedTable),
      registeredSourceValues: modalData.registeredSourceValues,
      submittedSourcePayload,
    });
  };

  return (
    <Dialog
        open={isVisible}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <DialogContent
          className="max-w-4xl p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <DialogHeader className={styles.header}>
            <div className={styles.breadcrumb}>
              <Button type="button" variant="text" className={styles.backButton} onClick={handleBack}>
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
              <span className={styles.breadcrumbMuted}>Source Details</span>
              <span className={styles.breadcrumbSeparator}>&gt;</span>
              <span className={styles.breadcrumbCurrent}>Table List</span>
            </div>
          </DialogHeader>

          <DialogBody className={styles.body}>
            <div className={styles.toolbar}>
              <DialogTitle className={styles.title}>Table List</DialogTitle>
              <div className={styles.actions}>
                <div className={styles.searchWrap}>
                  <Search className={styles.searchIcon} />
                  <Input
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder="Search Table Name"
                    aria-label="Search table name"
                    className={styles.searchInput}
                  />
                </div>
                <Button type="button" className={styles.addButton} onClick={handleAddNewTable}>
                  <Plus className="h-4 w-4" />
                  New Table
                </Button>
              </div>
            </div>

            {/* Identity line — the breadcrumb says "Source Details > Table List"
                but never showed *which* source. Render the picked source's
                name + database/schema inline (all already in the overlay
                payload) so the user knows what they're configuring. */}
            {modalData?.sourceName && (
              <p className="-mt-2 mb-4 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{modalData.sourceName}</span>
                {[modalData.databaseName, modalData.schemaName]
                  .filter((part): part is string => Boolean(part && part.trim()))
                  .map((part) => ` · ${part}`)
                  .join('')}
              </p>
            )}

            <div className={styles.tableFrame}>
              <div className={styles.tableHeaderRow}>
                <span>Table name</span>
                <div className={styles.headerActions}>
                  <Button
                    type="button"
                    variant="text"
                    className={styles.expandAllButton}
                    onClick={toggleExpandAll}
                    disabled={pagedTables.length === 0}
                  >
                    <ChevronsUpDown className="h-4 w-4" />
                    Expand All
                  </Button>
                </div>
              </div>

              <RadioGroup
                value={selectedTableId}
                onValueChange={setSelectedTableId}
                className={styles.tableList}
              >
                {isPending && (
                  <>
                    <Skeleton className={styles.tableSkeleton} />
                    <Skeleton className={styles.tableSkeleton} />
                  </>
                )}

                {!isPending && error && (
                  <p className={styles.stateText}>
                    {error instanceof Error ? error.message : "Failed to load registered tables."}
                  </p>
                )}

                {!isPending && !error && pagedTables.length === 0 && (
                  <p className={styles.stateText}>No registered tables found.</p>
                )}

                {!isPending && !error && pagedTables.map((table, index) => {
                  const isExpanded = expandedTableIds.includes(table.source_file_id);
                  const tableValue = String(table.source_file_id);
                  const fieldPage = fieldPageByTable[table.source_file_id] ?? 1;
                  const totalFieldPages = Math.max(1, Math.ceil(table.fields.length / FIELD_ROWS_PER_PAGE));
                  const fieldStart = (fieldPage - 1) * FIELD_ROWS_PER_PAGE;
                  const visibleFields = table.fields.slice(fieldStart, fieldStart + FIELD_ROWS_PER_PAGE);

                  return (
                    <div
                      key={table.source_file_id}
                      className={`${styles.tableItem} ${isExpanded ? styles.tableItemExpanded : ""}`}
                    >
                      <div className={styles.tableSummary}>
                        <RadioGroupItem
                          value={tableValue}
                          className={styles.radioItem}
                          aria-label={`Select ${table.table_name}`}
                        />
                        <span className={styles.tableName}>
                          {index + 1 + (page - 1) * rowsPerPage}. {table.table_name}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          className={styles.iconAction}
                          onClick={() => toggleTable(table)}
                        >
                          <ChevronsUpDown className="h-4 w-4" />
                          <span>{isExpanded ? "Collapse" : "Expand"}</span>
                        </Button>
                      </div>

                      {isExpanded && (
                        <div className={styles.fieldsTable}>
                          <Table>
                            <TableHeader className={styles.fieldsHeader}>
                              <TableRow>
                                <TableHead>
                                  Field Name <span className={styles.sortIcon}>↑↓</span>
                                </TableHead>
                                <TableHead>
                                  Data Type <span className={styles.sortIcon}>↑↓</span>
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {visibleFields.map((field) => (
                                <TableRow key={`${table.source_file_id}-${field.field_name}`}>
                                  <TableCell>{field.field_name}</TableCell>
                                  <TableCell>{field.data_type}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}

                      {isExpanded && (
                        <div className={styles.expandedPagination}>
                          <div className={styles.paginationGroup}>
                            <span>Rows per page</span>
                            <Select value={String(FIELD_ROWS_PER_PAGE)} disabled>
                              <SelectTrigger className={styles.rowsSelect}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={String(FIELD_ROWS_PER_PAGE)}>
                                  {FIELD_ROWS_PER_PAGE}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <span className={styles.pageText}>
                            Page {fieldPage} of {totalFieldPages}
                          </span>

                          <div className={styles.pageControls}>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className={styles.pageButton}
                              disabled={fieldPage <= 1}
                              onClick={() =>
                                setFieldPageByTable((current) => ({ ...current, [table.source_file_id]: 1 }))
                              }
                              aria-label="First field page"
                            >
                              <ChevronsLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className={styles.pageButton}
                              disabled={fieldPage <= 1}
                              onClick={() => handleFieldPageChange(table.source_file_id, -1, totalFieldPages)}
                              aria-label="Previous field page"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className={styles.pageButton}
                              disabled={fieldPage >= totalFieldPages}
                              onClick={() => handleFieldPageChange(table.source_file_id, 1, totalFieldPages)}
                              aria-label="Next field page"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className={styles.pageButton}
                              disabled={fieldPage >= totalFieldPages}
                              onClick={() =>
                                setFieldPageByTable((current) => ({
                                  ...current,
                                  [table.source_file_id]: totalFieldPages,
                                }))
                              }
                              aria-label="Last field page"
                            >
                              <ChevronsRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </RadioGroup>
            </div>

            <div className={styles.paginationBar}>
              <div className={styles.paginationGroup}>
                <span>Rows per page</span>
                <Select value={String(rowsPerPage)} onValueChange={handleRowsPerPageChange}>
                  <SelectTrigger className={styles.rowsSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROWS_PER_PAGE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={String(option)}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <span className={styles.pageText}>
                Page {page} of {totalPages}
              </span>

              <div className={styles.pageControls}>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={styles.pageButton}
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                  aria-label="First page"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={styles.pageButton}
                  disabled={page <= 1}
                  onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={styles.pageButton}
                  disabled={page >= totalPages}
                  onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={styles.pageButton}
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                  aria-label="Last page"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogBody>

          <DialogFooter className={styles.footer}>
            <Button
              type="button"
              variant="outline"
              className={styles.cancelButton}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={styles.saveButton}
              disabled={!selectedTableId || finalizeRegisteredSource.isPending}
              onClick={handleSave}
            >
              {finalizeRegisteredSource.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegisteredSourcesTableList;
